import adhdReportImg from '@/assets/images/adhd-report.webp';
import blankReportImg from '@/assets/images/blank-report.webp';
import { GeneratedTemplateCover } from '@/components/modals/GeneratedTemplateCover';
import { cn } from '@/lib/utils';
import { ReportTemplate } from '@/types/report';

type CardImage = { src: string; width: number; height: number };

/** Shared frame, so an authored illustration and a generated cover occupy
    exactly the same space and the grid rows stay aligned. */
const PLATE_CLASS = 'mb-3 aspect-video overflow-hidden rounded-lg';

const BLANK_CARD_IMAGE: CardImage = { src: blankReportImg, width: 780, height: 674 };

/** Per-template art, keyed by catalog slug. Only catalog templates carry a slug
    (it is unique and non-null there); user templates never match, since their
    art cannot be authored ahead of time — they fall through to a cover
    generated from their id instead. */
const CATALOG_CARD_IMAGES: Record<string, CardImage> = {
    'tdah-adulto': { src: adhdReportImg, width: 780, height: 674 },
};

/**
 * The 16:9 plate for an authored illustration. The art floats on transparency
 * rather than filling the frame, so it is contained and centered on the plate —
 * the same treatment the anamnesis cards give their illustrations.
 */
const CardImagePlate = ({ image }: { image: CardImage }) => (
    <div className={cn(PLATE_CLASS, 'flex items-center justify-center bg-muted/40')}>
        <img
            src={image.src}
            alt=""
            width={image.width}
            height={image.height}
            decoding="async"
            className="size-full object-contain p-2"
        />
    </div>
);

export const TemplateCardImage = ({ template }: { template: ReportTemplate }) => {
    const image = template.slug ? CATALOG_CARD_IMAGES[template.slug] : undefined;

    if (image) return <CardImagePlate image={image} />;

    return (
        <div className={PLATE_CLASS}>
            <GeneratedTemplateCover template={template} />
        </div>
    );
};

export const BlankCardImage = () => <CardImagePlate image={BLANK_CARD_IMAGE} />;
